import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import productService from "../services/productService";
import { toast } from "react-toastify";

/**
 * Create a new product
 */
export const createProduct = createAsyncThunk(
  "product/create",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await productService.createProduct(productData);
      toast.success("Product created successfully");
      return response.product;
    } catch (error) {
      toast.error(error.message || "Failed to create product");
      return rejectWithValue(error.message || "Failed to create product");
    }
  }
);

/**
 * Get all products
 */
export const getAllProducts = createAsyncThunk(
  "product/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await productService.getAllProducts(params);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to fetch products");
      return rejectWithValue(error.message || "Failed to fetch products");
    }
  }
);

/**
 * Get single product by ID
 */
export const getProductById = createAsyncThunk(
  "product/getById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.getProductById(productId);
      return response.product;
    } catch (error) {
      toast.error(error.message || "Failed to fetch product");
      return rejectWithValue(error.message || "Failed to fetch product");
    }
  }
);

/**
 * Update product
 */
export const updateProduct = createAsyncThunk(
  "product/update",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await productService.updateProduct(
        productId,
        productData
      );
      toast.success("Product updated successfully");
      return response.product;
    } catch (error) {
      toast.error(error.message || "Failed to update product");
      return rejectWithValue(error.message || "Failed to update product");
    }
  }
);

/**
 * Delete product
 */
export const deleteProduct = createAsyncThunk(
  "product/delete",
  async (productId, { rejectWithValue }) => {
    try {
      await productService.deleteProduct(productId);
      toast.success("Product deleted successfully");
      return productId;
    } catch (error) {
      toast.error(error.message || "Failed to delete product");
      return rejectWithValue(error.message || "Failed to delete product");
    }
  }
);

/**
 * Get active products
 */
export const getActiveProducts = createAsyncThunk(
  "product/getActive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productService.getActiveProducts();
      return response.products;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch active products");
    }
  }
);

// Initial state
const initialState = {
  products: [],
  currentProduct: null,
  activeProducts: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
  },
  loading: false,
  error: null,
  createLoading: false,
  createSuccess: false,
  updateLoading: false,
  updateSuccess: false,
  activeProductsLoading: false,
};

// Slice
const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    resetCreate: (state) => {
      state.createSuccess = false;
    },
    resetUpdate: (state) => {
      state.updateSuccess = false;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.products = [action.payload, ...state.products];
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload;
      })
      // Get All Products
      .addCase(getAllProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Product By ID
      .addCase(getProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(getProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.products = state.products.map((product) =>
          product._id === action.payload._id ? action.payload : product
        );
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload;
      })
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(
          (product) => product._id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Active Products
      .addCase(getActiveProducts.pending, (state) => {
        state.activeProductsLoading = true;
        state.error = null;
      })
      .addCase(getActiveProducts.fulfilled, (state, action) => {
        state.activeProductsLoading = false;
        state.activeProducts = action.payload;
        state.error = null;
      })
      .addCase(getActiveProducts.rejected, (state, action) => {
        state.activeProductsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentProduct, resetCreate, resetUpdate, clearErrors } =
  productSlice.actions;
export default productSlice.reducer;

